import { useEffect, useRef, useState } from "react";
import type { Sheet, SheetId } from "@electro-planner/domain";

type Props = {
  sheets: ReadonlyArray<Sheet>;
  activeSheetId: SheetId;
  onSelect: (id: SheetId) => void;
  onAdd: () => void;
  onRename: (id: SheetId, title: string) => void;
  /** Returnerar false om radering blockeras (t.ex. sista arket). */
  onDelete: (id: SheetId) => boolean;
  onMove: (id: SheetId, newIndex: number) => void;
  /** Klonar hela arket (symboler, trådar, kablar) till ett nytt "(kopia)"-ark. */
  onDuplicate: (id: SheetId) => void;
};

/**
 * Tab-rad nere i editorn (à la Excel). Dubbelklick på tab för att byta namn.
 * Håll shift vid knapp för move vänster/höger om vi vill lägga till senare;
 * för MVP räcker det att vänsterklick byter aktivt ark, "+" lägger till nytt.
 * Radera/flytta finns på hover-knappar och i kommandopaletten.
 */
export function SheetTabs({
  sheets,
  activeSheetId,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  onMove,
  onDuplicate,
}: Props) {
  return (
    <div
      role="tablist"
      aria-label="Ark"
      className="no-print flex h-9 shrink-0 items-stretch gap-0 overflow-x-auto border-t border-[var(--color-border)] bg-[var(--color-surface)] px-1"
    >
      {sheets.map((s, i) => (
        <SheetTab
          key={s.id}
          sheet={s}
          active={s.id === activeSheetId}
          canMoveLeft={i > 0}
          canMoveRight={i < sheets.length - 1}
          canDelete={sheets.length > 1}
          onSelect={() => onSelect(s.id)}
          onRename={(title) => onRename(s.id, title)}
          onDelete={() => onDelete(s.id)}
          onMoveLeft={() => onMove(s.id, i - 1)}
          onMoveRight={() => onMove(s.id, i + 1)}
          onDuplicate={() => onDuplicate(s.id)}
        />
      ))}
      <button
        type="button"
        onClick={onAdd}
        title="Nytt ark"
        aria-label="Lägg till nytt ark"
        className="my-1 ml-1 flex h-7 w-7 shrink-0 items-center justify-center rounded border border-[var(--color-border)] bg-white text-sm text-[var(--color-muted)] hover:text-[var(--color-fg)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
      >
        +
      </button>
    </div>
  );
}

type TabProps = {
  sheet: Sheet;
  active: boolean;
  canMoveLeft: boolean;
  canMoveRight: boolean;
  canDelete: boolean;
  onSelect: () => void;
  onRename: (title: string) => void;
  onDelete: () => boolean;
  onMoveLeft: () => void;
  onMoveRight: () => void;
  onDuplicate: () => void;
};

function SheetTab({
  sheet,
  active,
  canMoveLeft,
  canMoveRight,
  canDelete,
  onSelect,
  onRename,
  onDelete,
  onMoveLeft,
  onMoveRight,
  onDuplicate,
}: TabProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sheet.title);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!editing) setDraft(sheet.title);
  }, [editing, sheet.title]);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  function commit() {
    const trimmed = draft.trim();
    setEditing(false);
    if (!trimmed || trimmed === sheet.title) {
      setDraft(sheet.title);
      return;
    }
    onRename(trimmed);
  }

  return (
    <div
      role="tab"
      aria-selected={active}
      className={[
        "group relative my-1 flex shrink-0 items-center gap-1 rounded border px-2 py-1 text-sm transition-colors",
        active
          ? "border-[var(--color-accent)] bg-white text-[var(--color-fg)] font-medium"
          : "border-transparent bg-transparent text-[var(--color-muted)] hover:bg-white hover:text-[var(--color-fg)]",
      ].join(" ")}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditing(false);
              setDraft(sheet.title);
            }
          }}
          className="w-28 rounded border border-[var(--color-border)] bg-white px-1 text-sm outline-none focus:ring-2"
          aria-label={`Byt namn på ${sheet.title}`}
        />
      ) : (
        <button
          type="button"
          onClick={onSelect}
          onDoubleClick={() => setEditing(true)}
          className="truncate focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
          title={`${sheet.title} (dubbelklicka för att byta namn)`}
        >
          {sheet.title}
        </button>
      )}
      {!editing ? (
        <span className="ml-1 flex items-center opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
          <IconButton
            label={`Flytta ${sheet.title} åt vänster`}
            onClick={onMoveLeft}
            disabled={!canMoveLeft}
          >
            ‹
          </IconButton>
          <IconButton
            label={`Flytta ${sheet.title} åt höger`}
            onClick={onMoveRight}
            disabled={!canMoveRight}
          >
            ›
          </IconButton>
          <IconButton
            label={`Duplicera ${sheet.title}`}
            onClick={onDuplicate}
          >
            ⎘
          </IconButton>
          <IconButton
            label={`Radera ${sheet.title}`}
            onClick={onDelete}
            disabled={!canDelete}
          >
            ×
          </IconButton>
        </span>
      ) : null}
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      disabled={disabled}
      className="flex h-5 w-5 items-center justify-center rounded text-xs text-[var(--color-muted)] hover:bg-[var(--color-border)] hover:text-[var(--color-fg)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-30"
    >
      {children}
    </button>
  );
}
